/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnInit } from '@angular/core';
import {
  ADD_FAMILY_INFORMATION_FACADE_TOKEN,
  IAddFamilyInformationFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/add-family-information-facade-interface';
import { PropertiesListItemsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-properties-list-vm';
import { SysqueSwitchButtonVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-switch-button-vm';
import { SysqueDropdownConnectorsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-dropdown-connectors-vm';
import {
  AddFamilyInformationData,
  AddFamilyInformationReturnCodes,
  FamilyInformationItem,
} from '../../../models/family-information-model';
import { GlobalConstants } from '../../../constants/global-constants';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { IFamilyInformationItem } from '../add-family-information-types';
import { AddFamilyProjectDataInfo } from '../../../../../../utils/src/lib/common-helper/add-family-project-data-info';
import { AlertStatus, IAlert } from "../../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'sysque-junction-box-family-information',
  templateUrl: './junction-box-family-information.component.html',
  styleUrl: './junction-box-family-information.component.scss',
  standalone: false
})
export class JunctionBoxFamilyInformation implements OnInit {
  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public title = '';
  public image = '';
  public propertiesListItems: PropertiesListItemsVM[] = [];
  public isadditionalWireLengthVisible: boolean = true;
  public additionalWireLengthSpinButtons: boolean = true;
  public additionalWireLengthItems: SysqueNumberBoxVM[] = [];
  public connectorsTitle: string;
  public connectorsItems: SysqueDropdownConnectorsVM[] = [];
  public isSupportDisciplineVisible: boolean = false;
  public supportDisciplineLabel: string;
  public supportDiscipline: string;
  public instanceDescriptionLabel = '';
  public instanceDescription = '';
  public typeDescriptionLabel = '';
  public typeDescription = '';
  public projectDataButtonInfo: SysqueSwitchButtonVM;
  public item: IFamilyInformationItem;
  public isProjectDataBtnDisabled = false;
  public isAlertVisible = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }


  constructor(
    @Inject(ADD_FAMILY_INFORMATION_FACADE_TOKEN) private addFamilyInformationFacade: IAddFamilyInformationFacade) {}

  public ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
    this.addFamilyInformationFacade.registerControllers();
    const response = this.addFamilyInformationFacade.getFamilyInformationResponse();
    if (response.Data.IsFromAdd) {
      this.item = this.addFamilyInformationFacade.setItemToAdd(response.Data);
    } else {
      this.item = this.addFamilyInformationFacade.getFamilyInformationItem();
    }
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.PROJECT_PARAMS, []);
    this.setFamilyInformationData(response.Data, this.item);
    this.handleMessages(response);
    this.initData();
  }

  //#region data initialization
  private initData(): void {
    this.titleInit();
    this.imageInit();
    this.propertiesInit();
    this.addtionalWireLengthInit();
    this.connectorsInit();
    this.instanceDescriptionInit();
    this.typeDescriptionInit();
    this.projectDataInit();
  }

  private titleInit(): void {
    this.title = this.item.name;
  }

  private imageInit(): void {
    this.image = 'data:image/png;base64,' + this.item.image;
  }

  private propertiesInit(): void {
    this.propertiesListItems = [
      {
        label: GlobalConstants.ITEM_TYPE_LABEL,
        value: this.item.fittingType,
        isEditable: false,
      },
      {
        label: GlobalConstants.MANUFACTURER_LABEL,
        value: this.item.manufacturer,
        isEditable: false,
      },
      {
        label: GlobalConstants.ID_LABEL,
        value: this.item.id,
        isEditable: false,
      },
    ];
  }

  private addtionalWireLengthInit(): void {
    this.additionalWireLengthItems = [
      {
        label: GlobalConstants.ADDED_WIRE_LENGTH_LABEL,
        value: Number(this.item.addedWireLength),
        isMetric: this.isMetric,
        height: '30',
        width: '122',
        minValue: 0
      },
    ];
  }

  private connectorsInit(): void {
    if(this.item.connectorCount === 0) {
      return;
    }
    const itemConnectors = this.item.connectors;
    let selectedConnectors = [];
    if (itemConnectors) {
      selectedConnectors = itemConnectors?.split(' x ').map(x => x.trim());
    }
    this.connectorsTitle = '';
    const connectors = this.addFamilyInformationFacade.getJunctionBoxConnectors();
    const connectorsCount = this.item.connectorCount;
    for (let i = 0; i < connectorsCount; i++) {
      this.connectorsItems.push({
        label: '',
        data: connectors.map((x) => x.connector),
        selectedValue: Array.isArray(selectedConnectors)
        ? connectors?.find(x => x.connector === selectedConnectors[i])?.connector
              : connectors?.find(x => x.connector === selectedConnectors)?.connector,
        width: '152'
      });
    }
  }

  private instanceDescriptionInit(): void {
    this.instanceDescriptionLabel = GlobalConstants.INSTANCE_DESCRIPTION_LABEL;
    this.instanceDescription = this.item.descriptionInstance;
  }

  private typeDescriptionInit(): void {
    this.typeDescriptionLabel = GlobalConstants.TYPE_DESCRIPTION_LABEL;
    this.typeDescription = this.item.descriptionType;
  }

  private projectDataInit(): void {
    this.projectDataButtonInfo = {
      title: GlobalConstants.PROJECT_DATA_LABEL,
      enabled: Array.isArray(this.item.parameters) && this.item.parameters.length > 0,
      labelForEnabled: GlobalConstants.ENABLED,
      labelForDisabled: GlobalConstants.DISABLED,
      hasOptionButton: true,
    };
  }

  //#endregion

  private handleMessages(response: FamilyInformationItem): void {
    if (
      response.ReturnCode == AddFamilyInformationReturnCodes.Success ||
      response.ReturnCode == AddFamilyInformationReturnCodes.NoMessage
    ) {
      this.hideAlert();
    } else if (response.ReturnCode == AddFamilyInformationReturnCodes.InvalidFamilies) {
      this.showAlert(GlobalConstants.PLEASE_UPDATE_FAMILIES_AND_TRY_AGAIN, AlertStatus.Warning);
    }
  }

  public onPropertiesValueChanged($event: { value: string; label: string }): void {
    switch ($event.label) {
      case GlobalConstants.ITEM_TYPE_LABEL:
        this.item.partType = $event.value;
        break;
      case GlobalConstants.MANUFACTURER_LABEL:
        this.item.manufacturer = $event.value.toString();
        break;
      case GlobalConstants.ID_LABEL:
        this.item.id = $event.value.toString();
        break;
    }
  }

  public onAdditionalWireLengthValueChanged($event: { value: string; label: string }): void {
    this.item.addedWireLength = $event.value;
  }

  public onInstanceDescriptionValueChanged($event: string): void {
    this.item.descriptionInstance = $event;
  }

  public onTypeDescriptionValueChanged($event: string): void {
    this.item.descriptionType = $event;
  }

  public onConnectorValueChanged($event: { value: string; label: string; index: number }): void {
    let selectedConnectors;
    if(this.item.connectors === null) {
      selectedConnectors = [];
    } else {
      selectedConnectors = this.item.connectors.split(' x ').map((x) => x.trim());
    }
    selectedConnectors[$event.index] = $event.value;
    this.item.connectors = selectedConnectors.join(' x ');
  }

  public doneClick(): void {
    new AddFamilyProjectDataInfo(this.addFamilyInformationFacade).getModifiedProjectParameters(this.item, this.isProjectDataBtnDisabled);
    //dispatch saveFamilyInformationInXml - call the backend to save the information in the xml file
    this.addFamilyInformationFacade.saveFamilyInformationInXml(this.item);
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === 'familyInformationSaveResponse') {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response == AddFamilyInformationReturnCodes.Success) {
        this.addFamilyInformationFacade.updateItem(this.item);
        this.addFamilyInformationFacade.sendClosePopupAction();
      } else if (response == AddFamilyInformationReturnCodes.InvalidFamilies) {
        this.showAlert(GlobalConstants.PLEASE_UPDATE_FAMILIES_AND_TRY_AGAIN, AlertStatus.Warning);
      } else if (response == AddFamilyInformationReturnCodes.IdError) {
        this.showAlert(GlobalConstants.PLEASE_ENTER_ID_FIELD, AlertStatus.Warning);
      } else if (response == AddFamilyInformationReturnCodes.FamilyAlreadyAddedInSystem) {
        this.showAlert(GlobalConstants.THIS_FAMILY_IS_ALREADY_ADDED_IN_SYSTEM, AlertStatus.Warning);
      }
    }
  }

  public onProjectDataButtonClicked(): void {
    this.addFamilyInformationFacade.onClickedToOpenProjectData();
  }

  public onProjectDataSwitchClicked(button: SysqueSwitchButtonVM): void {
    this.isProjectDataBtnDisabled = !button.enabled;
  }

  //#region alert
  private showAlert(message: string, alertStatus: AlertStatus): void {
    this.warningMessage = message;
    this.alertStatus = alertStatus;
    this.isAlertVisible = true;
  }

  public hideAlert(): void {
    this.warningMessage = '';
    this.isAlertVisible = false;
  }

  //#endregion

  private get isMetric(): boolean {
    return this.item.unit === GlobalConstants.METRIC;
  }

  private setFamilyInformationData(data: AddFamilyInformationData, item: IFamilyInformationItem): void {
    item.partType = data.PartType;
    item.fittingType = data.FittingType;
    item.isClearence = data.IsClearance;
    item.isEquipment = data.IsEquipment;
    item.connectorCount = data.ConnectorCount;
    item.csvName = data.CsvName;
    item.centerRadius = data.CenterRadius;
    item.diameter = data.Diameter;
    item.height = data.Height;
    item.width = data.Width;
    if (!data.Manufacturer) {
      item.manufacturer = data.Manufacturer;
    }
    item.familyCategoryName = data.FamilyCategoryName;
    item.jBoxType = data.JBoxType;
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, item);
  }  
}
